/**
 * GroupMediaUpload — renders one MediaUploadGrid per visible group
 * in a vertical layout with colored group headers.
 */

import React from 'react';
import MediaUploadGrid from './MediaUploadGrid';
import { getGroupLabel } from '../../utils/groupUtils';

const GROUP_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-500', text: 'text-blue-700', dot: 'bg-blue-500' },
  { bg: 'bg-purple-50', border: 'border-purple-200', header: 'bg-purple-500', text: 'text-purple-700', dot: 'bg-purple-500' },
  { bg: 'bg-teal-50', border: 'border-teal-200', header: 'bg-teal-500', text: 'text-teal-700', dot: 'bg-teal-500' },
  { bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-500', text: 'text-amber-700', dot: 'bg-amber-500' },
  { bg: 'bg-rose-50', border: 'border-rose-200', header: 'bg-rose-500', text: 'text-rose-700', dot: 'bg-rose-500' },
  { bg: 'bg-indigo-50', border: 'border-indigo-200', header: 'bg-indigo-500', text: 'text-indigo-700', dot: 'bg-indigo-500' },
];

export function getGroupColor(index: number) {
  return GROUP_COLORS[index % GROUP_COLORS.length];
}

interface GroupMediaUploadProps {
  groupCount: number;
  visibleGroups?: number[];
  groupMedia: Record<string, string[]>;
  onMediaAdd: (groupIndex: number, url: string) => void;
  onMediaRemove: (groupIndex: number, removeIndex: number) => void;
  onMediaReplace?: (groupIndex: number, mediaIndex: number, newUrl: string) => void;
  maxItemsPerGroup?: number;
  disabled?: boolean;
  compact?: boolean;
  /**
   * Forwarded to every group's grid. Defaults to true (the historical behaviour of every caller);
   * MaxDiff claim images pass false, because the surface that renders them is the ranking button
   * itself and cannot hold a player.
   */
  allowVideo?: boolean;
}

const GroupMediaUpload: React.FC<GroupMediaUploadProps> = ({
  groupCount,
  visibleGroups,
  groupMedia,
  onMediaAdd,
  onMediaRemove,
  onMediaReplace,
  maxItemsPerGroup = 5,
  disabled = false,
  compact = true,
  allowVideo = true,
}) => {
  const indices = visibleGroups ?? Array.from({ length: groupCount }, (_, i) => i);

  return (
    <div className="space-y-2">
      {indices.map((idx) => {
        const urls = groupMedia[String(idx)] || [];
        const color = getGroupColor(idx);
        return (
          <div key={idx} className={`rounded-lg overflow-hidden border ${color.border} ${color.bg}`}>
            <div className={`${color.header} px-2.5 py-1 flex items-center gap-1.5`}>
              <span className="text-[11px] font-semibold text-white tracking-wide uppercase">
                {getGroupLabel(idx)}
              </span>
              {urls.length > 0 && (
                <span className="ml-auto text-[10px] text-white/80">{urls.length} file{urls.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="p-2">
              <MediaUploadGrid
                mediaUrls={urls}
                onMediaAdd={(url) => onMediaAdd(idx, url)}
                onMediaRemove={(removeIdx) => onMediaRemove(idx, removeIdx)}
                onMediaReplace={
                  onMediaReplace
                    ? (mediaIndex, newUrl) => onMediaReplace(idx, mediaIndex, newUrl)
                    : undefined
                }
                maxItems={maxItemsPerGroup}
                disabled={disabled}
                compact={compact}
                allowVideo={allowVideo}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GroupMediaUpload;
